import { Box, styled, SxProps, Theme, Tooltip, useTheme } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { CSSProperties } from 'react';

import { AircraftData } from '#entities';

import {
  getAircraftStatus,
  getAircraftTimeSeverity,
  getAirportLabel,
  getMaintenanceAccent,
  getTimeSeverityFromCrewDuty,
} from '../../helpers';

const PREFIX = 'TSMED-AircraftCard';

export const aircraftCardClasses = {
  header: `${PREFIX}-header`,
  headerIcon: `${PREFIX}-headerIcon`,
  headerRegistration: `${PREFIX}-headerRegistration`,
  statusLabel: `${PREFIX}-statusLabel`,
  crew: `${PREFIX}-crew`,
  crewNext: `${PREFIX}-crewNext`,
  crewItem: `${PREFIX}-crewItem`,
  airportCodes: `${PREFIX}-airportCodes`,
  airportCode: `${PREFIX}-airportCode`,
} as const;

type RootProps = {
  fullSize?: boolean;
  stretch?: boolean;
};
const excludedProps = ['fullSize', 'stretch'] as const;

const Root = styled(Box, {
  name: PREFIX,
  shouldForwardProp: p =>
    !excludedProps.includes(p as (typeof excludedProps)[number]),
})<RootProps>(({
  theme: { spacing, palette, mixins, utils },
  fullSize = false,
}) => {
  const clamp = (min: number, max: number, widgetMax?: number) =>
    fullSize ? utils.clamp(min, max) : utils.clamp(min, widgetMax ?? max);

  return {
    '--pad': fullSize ? spacing(1.5) : spacing(1),
    '--gap-xs': clamp(2, 5, 4),
    '--gap-sm': clamp(4, 8, 6),
    '--gap-md': clamp(6, 12, 8),

    display: 'flex',
    border: '1px solid',
    borderColor: `var(--cardBorderColor, ${palette.grey[700]})`,
    borderRadius: spacing(2.5, 0.5),
    flexDirection: 'column',
    padding: 'var(--pad)',
    color: 'var(--primaryColor)',
    width: 'var(--card-w, 100%)',
    height: '100%',
    minHeight: 'var(--card-min-h, 150px)',
    maxWidth: '100%',
    minWidth: 0,

    fontSize: clamp(16, 26, 20),
    fontFamily: "'JetBrains Mono', monospace",
    lineHeight: spacing(4),
    cursor: 'pointer',
    boxSizing: 'border-box',

    ...mixins.aircraftStatusStyles(palette),
    ...mixins.aircraftStatusStyles(palette, { prefix: `&` }),
    ...mixins.severityStyles(palette),
    ...mixins.severityStyles(palette, { prefix: `&` }),

    [`.${aircraftCardClasses.header}`]: {
      color: 'inherit',
      display: 'flex',
      alignItems: 'center',
      fontSize: clamp(16, 26, 20),
      fontWeight: 500,

      [`.${aircraftCardClasses.headerIcon}`]: {
        color: 'inherit',
        margin: spacing(-1),
        height: spacing(5),
        width: spacing(5),
        fill: 'currentColor',
      },
      [`.${aircraftCardClasses.headerRegistration}`]: {
        color: 'var(--tailColor, inherit)',
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0,
      },
    },

    [`.${aircraftCardClasses.statusLabel}`]: {
      fontSize: clamp(16, 24, 20),
      flexGrow: fullSize ? 0 : 1,
      color: `var(--statusLabelColor, var(--organColor, var(--aircraftStatusMaintenanceColor, ${palette.grey[100]})))`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      ...mixins.organStyles(palette, { prefix: `&` }),
    },

    [`.${aircraftCardClasses.crew}`]: {
      position: 'relative',
      isolation: 'isolate',
      flex: 1,
      ['.overlay']: {
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(2px) brightness(0.5)',
        WebkitBackdropFilter: 'blur(2px) brightness(0.5)',
        fontSize: clamp(16, 28, 18),
        lineHeight: clamp(16, 28, 18),
        color: 'white',
        textAlign: 'center',
        padding: fullSize ? 0 : spacing(1),
        boxSizing: 'border-box',
        transition: 'backdrop-filter 0.2s ease-out',
      },
      ul: {
        position: 'relative',
        zIndex: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        gap: 'var(--gap-xs)',
        flexDirection: 'column',
        fontSize: clamp(16, 24, 18),
        color: palette.grey[700],
        alignItems: 'flex-start',
        overflow: 'hidden',
        minWidth: 0,
      },
      [`.${aircraftCardClasses.crewItem}`]: {
        fontSize: clamp(16, 24, 18),
        lineHeight: clamp(20, 35, 30),
        backgroundColor: palette.grey[900],
        padding: spacing(0.25, 1),
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: 'var(--primaryColor)',
        background: 'var(--secondaryColor)',
        ...mixins.severityStyles(palette, {
          prefix: `&`,
          primaryOpacity: 0.5,
          secondaryOpacity: 0.1,
        }),
      },
    },

    ['&:hover']: {
      [`.${aircraftCardClasses.crew}`]: {
        ['.overlay']: {
          backdropFilter: 'blur(2px) brightness(0.75)',
          WebkitBackdropFilter: 'blur(2px) brightness(0.75)',
        },
      },
      [`.${aircraftCardClasses.crewItem}`]: {
        ...mixins.severityStyles(palette, {
          prefix: `&`,
          primaryOpacity: 1,
          secondaryOpacity: 0.2,
        }),
      },
    },

    [`.${aircraftCardClasses.airportCodes}`]: {
      display: 'flex',
      gap: 'var(--gap-sm)',
      lineHeight: clamp(18, 30, 24),
      fontSize: clamp(16, 26, 18),
      opacity: 0.5,
      color: palette.grey[200],
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      [`.${aircraftCardClasses.airportCode}`]: {
        padding: spacing(0.25, 1),
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
    },
  };
});

export interface AircraftCardProps {
  aircraftData: AircraftData;
  availabilityGroupName: string | null;
  fullSize?: boolean;
  stretch?: boolean;
  sx?: SxProps<Theme>;
}

export const CrewAvailabilityCard = ({
  aircraftData,
  availabilityGroupName = null,
  fullSize = false,
  stretch = false,
  sx,
}: AircraftCardProps) => {
  const {
    CrewDuties,
    registration,
    actualLabel,
    actualOrgan,
    airportLastKnown,
    airportArrival,
    maintenanceType,
    isOnFlight,
    dutyRanges,
  } = aircraftData;

  const theme = useTheme();
  const maintenanceAccent = getMaintenanceAccent(
    maintenanceType,
    theme.palette,
  );
  const isMaintenance = Boolean(maintenanceAccent);
  const timezone = airportLastKnown?.IanaTimeZone;
  const actualTimeSeverity = getAircraftTimeSeverity(aircraftData);
  const actualStatus = getAircraftStatus(aircraftData);

  const crewDuties = CrewDuties.filter(cd =>
    dayjs(cd.OffDutyDateTime).tz(timezone).isAfter(dayjs()),
  ).slice(0, 2);

  const nextDutyRange = dutyRanges.find(
    dr => dr[0] > new Date().getTime() + 2 * 60 * 60 * 1000,
  );

  const lastKnownLabel = getAirportLabel(
    airportLastKnown?.IATA,
    airportLastKnown?.ICAO,
  );
  const arrivalLabel = getAirportLabel(
    airportArrival?.IATA,
    airportArrival?.ICAO,
  );

  const isNextCrew =
    dutyRanges.length &&
    dutyRanges.every(dr => dr[0] > new Date().getTime() + 2 * 60 * 60 * 1000);

  const actualCrewDuties =
    crewDuties?.map(crewDuty => ({
      name: [
        crewDuty.Crew.Contact.FirstNames.split(' ')
          .map(n => n[0] && `${n[0]}.`)
          .filter(Boolean)
          .join(''),
        crewDuty.Crew.Contact.LastName,
      ].join(' '),
      timeSeverity: getTimeSeverityFromCrewDuty(crewDuty, timezone),
    })) || [];

  const accentVars = isMaintenance
    ? {
        // status label chain resolves to this first
        ['--statusLabelColor']: maintenanceAccent,
        // header registration uses var(--tailColor, inherit)
        ['--tailColor']: maintenanceAccent,
        // fallback var in the chain, if something removes --statusLabelColor
        ['--aircraftStatusMaintenanceColor']: maintenanceAccent,
        // border/hover tint
        ['--cardBorderColor']: maintenanceAccent,
      }
    : undefined;

  return (
    <Root
      fullSize={fullSize}
      stretch={stretch}
      style={accentVars as CSSProperties}
      className={classNames(`aircraft-status-${actualStatus}`, {
        [`severity-status-${actualTimeSeverity}`]: !maintenanceType,
        [`maintenance-type-${maintenanceType?.OpsBoardBadge}`]:
          !!maintenanceType,
      })}
      sx={sx}
    >
      <Box className={aircraftCardClasses.header}>
        <Box className={aircraftCardClasses.headerRegistration}>
          <span>{registration}</span>
          <span>{availabilityGroupName}</span>
        </Box>
      </Box>

      <Tooltip
        title={actualLabel ?? (isOnFlight ? 'In Transit' : 'On Ground')}
        arrow
      >
        <Box
          className={classNames(
            aircraftCardClasses.statusLabel,
            // don't attach organ class while on maintenance,
            !isMaintenance && `organ-type-${actualOrgan}`,
          )}
        >
          {actualLabel ?? (isOnFlight ? 'In Transit' : 'On Ground')}
        </Box>
      </Tooltip>
      <Box
        className={classNames(aircraftCardClasses.crew, {
          [aircraftCardClasses.crewNext]: isNextCrew,
        })}
      >
        {isNextCrew ? (
          <Box className={'overlay'}>
            {dayjs(nextDutyRange?.[0]).tz(timezone).format('HH:mm z')} <br />(
            {dayjs(nextDutyRange?.[0]).tz(timezone).from(dayjs().tz(timezone))})
          </Box>
        ) : null}
        <ul>
          {actualCrewDuties.map((c, index) => (
            <Tooltip
              key={`${index} + ${c?.name ?? ''}`}
              title={c?.name ?? 'N/A'}
              arrow
            >
              <li
                className={classNames(aircraftCardClasses.crewItem, {
                  [`severity-status-${c.timeSeverity}`]: !!c?.timeSeverity,
                })}
              >
                {c?.name ?? 'N/A'}
              </li>
            </Tooltip>
          ))}
        </ul>
      </Box>
      <Box className={aircraftCardClasses.airportCodes}>
        <Box className={aircraftCardClasses.airportCode}>{lastKnownLabel}</Box>
        {airportArrival ? (
          <Box className={aircraftCardClasses.airportCode}>{arrivalLabel}</Box>
        ) : null}
      </Box>
    </Root>
  );
};
